'use client';

import { ActivityBuffer } from '@/lib/activity';
import { recordActivity } from '@/lib/api';
import { getAuthToken } from '@/lib/auth';

/**
 * The page-wide activity buffer.
 *
 * A single shared instance, because two would each flush half a buyer's history and
 * the derivation would see a narrower search than the real one.
 */
const buffer = new ActivityBuffer((signals) => recordActivity(signals));

/**
 * Whether we may record at all.
 *
 * Anonymous browsing is deliberately never buffered rather than buffered and sent
 * on sign-in. Holding somebody's history on the chance they later create an account
 * is exactly the posture this feature has to avoid.
 */
async function signedIn(): Promise<boolean> {
  try {
    return Boolean(await getAuthToken());
  } catch {
    return false;
  }
}

export async function trackView(listing: {
  id: string;
  city?: string;
  price?: number;
  beds?: number;
  propertyType?: string;
}): Promise<void> {
  if (!(await signedIn())) return;
  await buffer.recordView(listing);
}

export async function trackSearch(filters: {
  cities?: string[];
  price_min?: number;
  price_max?: number;
  beds_min?: number;
  property_types?: string[];
}): Promise<void> {
  if (!(await signedIn())) return;
  await buffer.recordSearch(filters);
}

/** Send whatever is buffered. Bound to page-hide so a session's browsing lands. */
export function flushActivity(): void {
  void buffer.flush();
}

if (typeof window !== 'undefined') {
  // `visibilitychange` rather than `beforeunload`: mobile browsers frequently skip
  // the latter entirely, which would silently discard the last session's signals —
  // exactly the buyers this feature is for.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushActivity();
  });
}
